'use client';

import Image, { ImageProps } from 'next/image';
import React, { useState, useCallback } from 'react';
import contentfulLoader from '@/components/ui/image/contentfulLoader';

function getPlaceholderUrl(src: string | object): string | null {
  if (typeof src !== 'string') return null;
  if (!src.includes('images.ctfassets.net')) return null;
  const separator = src.includes('?') ? '&' : '?';
  return `${src}${separator}w=20&q=1&fm=webp`;
}

type ContentfulImageProps = ImageProps & {
  focalX?: number | null;
  focalY?: number | null;
};

export default function ContentfulImage({ onLoad, src, alt, focalX = 50, focalY = 50, style, ...rest }: ContentfulImageProps) {
  const objectPosition = `${focalX ?? 50}% ${focalY ?? 50}%`;
  const [loaded, setLoaded] = useState(false);
  const placeholderUrl = getPlaceholderUrl(src);

  const handleLoad = useCallback<React.ReactEventHandler<HTMLImageElement>>(
    (e) => {
      setLoaded(true);
      onLoad?.(e);
    },
    [onLoad]
  );

  if (!placeholderUrl) {
    return <Image src={src} alt={alt} loader={contentfulLoader} onLoad={onLoad} style={{ objectPosition, ...style }} {...rest} />;
  }

  return (
    <>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${placeholderUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: objectPosition,
          filter: 'blur(20px)',
          transform: 'scale(1.1)',
          borderRadius: 'inherit',
          opacity: loaded ? 0 : 1,
          transition: 'opacity 0.4s ease-out',
          pointerEvents: 'none',
        }}
      />
      <Image src={src} alt={alt} loader={contentfulLoader} onLoad={handleLoad} style={{ objectPosition, ...style }} {...rest} />
    </>
  );
}
